import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, onSnapshot, addDoc, doc, updateDoc, deleteDoc, query } from 'firebase/firestore';
import { FaPlus, FaEdit, FaTrash, FaBoxes, FaExclamationTriangle } from 'react-icons/fa';
import toast, { Toaster } from 'react-hot-toast';

function ItemFormModal({ item, onClose, onSave, title }) {
    const [formData, setFormData] = useState({
        nombre: item?.nombre || '',
        cantidad: item?.cantidad || 0,
        stockMinimo: item?.stockMinimo || 0,
        precio: item?.precio || 0,
    });
    const [isSaving, setIsSaving] = useState(false);

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'number' ? parseFloat(value) || 0 : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if(!formData.nombre) return toast.error("El nombre del artículo es obligatorio.");
        setIsSaving(true);
        await onSave(formData);
        setIsSaving(false);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-2xl w-full max-w-md">
                <h2 className="text-xl sm:text-2xl font-bold mb-6 text-gray-800">{item ? `Editar ${title}` : `Añadir Nuevo ${title}`}</h2>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-gray-700 font-semibold mb-2">Nombre del Artículo</label>
                        <input type="text" name="nombre" value={formData.nombre} onChange={handleChange} className="w-full p-3 border rounded-lg" required />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                        <div><label className="block text-gray-700 font-semibold mb-2">Stock</label><input type="number" name="cantidad" value={formData.cantidad} onChange={handleChange} className="w-full p-3 border rounded-lg" min="0" required /></div>
                        <div><label className="block text-gray-700 font-semibold mb-2">Stock Mín.</label><input type="number" name="stockMinimo" value={formData.stockMinimo} onChange={handleChange} className="w-full p-3 border rounded-lg" min="0" /></div>
                        <div><label className="block text-gray-700 font-semibold mb-2">Precio (€)</label><input type="number" name="precio" value={formData.precio} onChange={handleChange} className="w-full p-3 border rounded-lg" min="0" step="0.01" /></div>
                    </div>
                    <div className="flex flex-col-reverse sm:flex-row justify-end gap-4 mt-6">
                        <button type="button" onClick={onClose} className="w-full sm:w-auto px-6 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400" disabled={isSaving}>Cancelar</button>
                        <button type="submit" className="w-full sm:w-auto px-6 py-2 bg-teal-500 text-white font-semibold rounded-lg hover:bg-teal-600 flex items-center justify-center" disabled={isSaving}>
                            {isSaving ? 'Guardando...' : (item ? 'Guardar Cambios' : 'Añadir Artículo')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function AccesoriosView() {
    const [inventario, setInventario] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    useEffect(() => {
        const q = query(collection(db, "accesorios"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setInventario(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const handleSaveItem = async (itemData) => {
        const toastId = toast.loading(editingItem ? 'Actualizando...' : 'Añadiendo...');
        try {
            if (editingItem) {
                await updateDoc(doc(db, "accesorios", editingItem.id), itemData);
            } else {
                await addDoc(collection(db, "accesorios"), itemData);
            }
            toast.success('¡Artículo guardado!', { id: toastId });
        } catch(error) {
            console.error("Error al guardar:", error);
            toast.error('No se pudo guardar el artículo.', { id: toastId });
        }
        setEditingItem(null);
    };

    const handleEditItem = (item) => {
        setEditingItem(item);
        setIsModalOpen(true);
    };

    const handleDeleteItem = async (id) => {
        if (window.confirm("¿Estás seguro de que quieres eliminar este artículo?")) {
            const toastId = toast.loading('Eliminando...');
            try {
                await deleteDoc(doc(db, "accesorios", id));
                toast.success('Artículo eliminado.', { id: toastId });
            } catch(error) {
                console.error("Error al eliminar:", error);
                toast.error('No se pudo eliminar el artículo.', { id: toastId });
            }
        }
    };
    
    const valorTotalInventario = inventario.reduce((total, item) => total + ((item.cantidad || 0) * (item.precio || 0)), 0);

    return (
        <div className="h-full flex flex-col p-4 md:p-6 gap-6">
            <Toaster position="top-center" />
            {isModalOpen && <ItemFormModal item={editingItem} onClose={() => { setIsModalOpen(false); setEditingItem(null); }} onSave={handleSaveItem} title="Accesorio" />}
            
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">Inventario de Accesorios</h1>
                    <div className="bg-white p-4 rounded-lg shadow-md flex items-center">
                        <div className="p-3 mr-4 bg-blue-500 text-white rounded-full"><FaBoxes size={22} /></div>
                        <div>
                            <p className="text-sm text-gray-500">Valor Total</p>
                            <p className="text-xl sm:text-2xl font-bold text-gray-800">{valorTotalInventario.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</p>
                        </div>
                    </div>
                </div>
                <button onClick={() => setIsModalOpen(true)} className="w-full md:w-auto flex items-center justify-center gap-2 bg-teal-500 text-white font-semibold px-4 py-2 rounded-lg hover:bg-teal-600 shadow">
                    <FaPlus />Añadir Accesorio
                </button>
            </div>
            
            {/* Chad: Contenedor principal de la lista/tabla */}
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md flex-grow">
                {loading ? ( <p>Cargando inventario...</p> ) : (
                    <div>
                        {/* Encabezado de la tabla, visible solo en pantallas grandes (lg) */}
                        <div className="hidden lg:grid grid-cols-5 gap-4 mb-2 pb-2 border-b-2 border-gray-200">
                            <div className="font-semibold text-gray-600">Nombre</div>
                            <div className="font-semibold text-gray-600">Stock</div>
                            <div className="font-semibold text-gray-600">Stock Mín.</div>
                            <div className="font-semibold text-gray-600">Precio</div>
                            <div className="font-semibold text-gray-600 text-right">Acciones</div>
                        </div>
                        
                        {/* Lista de tarjetas para móvil, que se convierte en tabla para desktop */}
                        <div className="space-y-4 lg:space-y-0">
                            {inventario.map(item => {
                                const bajoStock = (item.stockMinimo > 0) && (item.cantidad <= item.stockMinimo);
                                return (
                                    <div key={item.id} className={`lg:grid lg:grid-cols-5 lg:gap-4 items-center p-4 lg:p-0 lg:py-3 rounded-lg lg:rounded-none border lg:border-0 lg:border-b ${bajoStock ? 'border-red-200 bg-red-50' : 'border-gray-200'} lg:bg-transparent`}>
                                        {/* Columna Nombre */}
                                        <div className="flex justify-between items-center lg:block">
                                            <span className="font-bold text-gray-800 lg:font-semibold">{item.nombre}</span>
                                            <div className="lg:hidden text-right">
                                                <button onClick={() => handleEditItem(item)} className="text-gray-500 hover:text-blue-500 p-2" title="Editar"><FaEdit /></button>
                                                <button onClick={() => handleDeleteItem(item.id)} className="text-gray-500 hover:text-red-500 p-2" title="Eliminar"><FaTrash /></button>
                                            </div>
                                        </div>
                                        
                                        {/* Datos adicionales en formato Fila para móvil */}
                                        <div className="grid grid-cols-3 gap-2 mt-2 lg:mt-0 lg:col-span-3 lg:grid-cols-3">
                                            <div className="lg:hidden text-xs text-gray-500">Stock</div>
                                            <div className="lg:hidden text-xs text-gray-500">Stock Mín.</div>
                                            <div className="lg:hidden text-xs text-gray-500">Precio</div>
                                            
                                            <div>
                                                <span className={`font-semibold ${bajoStock ? 'text-red-600' : ''}`}>
                                                    {item.cantidad}
                                                    {bajoStock && <FaExclamationTriangle className="inline ml-2 text-red-500" title="¡Stock bajo!" />}
                                                </span>
                                            </div>
                                            <div className="text-gray-600">{item.stockMinimo || 0}</div>
                                            <div>{item.precio?.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</div>
                                        </div>

                                        {/* Acciones visibles solo en Desktop */}
                                        <div className="hidden lg:block text-right whitespace-nowrap">
                                            <button onClick={() => handleEditItem(item)} className="text-gray-500 hover:text-blue-500 p-2" title="Editar"><FaEdit /></button>
                                            <button onClick={() => handleDeleteItem(item.id)} className="text-gray-500 hover:text-red-500 p-2" title="Eliminar"><FaTrash /></button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                 {inventario.length === 0 && !loading && (
                    <p className="text-center py-10 text-gray-500">No hay accesorios. ¡Añade el primero!</p>
                 )}
            </div>
        </div>
    );
}

export default AccesoriosView;